/**
 * プロフィール画面 - PACE個性化対応版
 * 手動入力（性別・興味・特性・経験）+ 自動分析（学習スタイル）
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { authAPI } from '../utils/api';
import { logClientError } from '../utils/safeLog';
import FuriganaText from './FuriganaText';
import { useFuriganaAria } from '../contexts/FuriganaContext';

// Render a translated string that carries <strong> markup with furigana over every
// kanji. <FuriganaText> can't recurse into a <Trans> custom component, so we split the
// (server-fixed) <strong>…</strong> markup ourselves and annotate each text part —
// preserving the bold. Used for the PACE-guide descriptive paragraphs.
function renderRichFurigana(str) {
  if (typeof str !== 'string') return str;
  const out = [];
  const re = /<strong>([\s\S]*?)<\/strong>/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(str)) !== null) {
    if (m.index > last) out.push(<FuriganaText key={`t${i}`} text={str.slice(last, m.index)} />);
    out.push(<strong key={`s${i}`}><FuriganaText text={m[1]} /></strong>);
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < str.length) out.push(<FuriganaText key={`t${i}`} text={str.slice(last)} />);
  return out;
}

// JS-side semantic alpha constants (App.css RGB triplet と組合せて 2-axis semantic 表現)
const ALPHA_BG_SUBTLE = 0.031;  // cogItem bg (08 hex byte)
const ALPHA_BG_LIGHT  = 0.094;  // axisBadge bg (18 hex byte)
const ALPHA_BG_MEDIUM = 0.125;  // severityBadge / severity list bg (20 hex byte)
const ALPHA_BORDER    = 0.188;  // cogItem border (30 hex byte)

// Backend stores grade_level as canonical Japanese strings; map to localized label keys
// (mirrors Register.js GRADE_VALUE_TO_KEY). Unknown values fall back to the raw stored string.
const GRADE_VALUE_TO_LABEL_KEY = {
  '中学1年': 'register.gradeJhs1',
  '中学2年': 'register.gradeJhs2',
  '中学3年': 'register.gradeJhs3',
  '教師': 'register.summaryTeacher',
};

/**
 * 教師向け PACE / 認知困難ガイド参考ページ
 */
function TeacherPACEGuide() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const sectionCard = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    boxShadow: 'var(--shadow-md)',
    borderRadius: '24px',
    padding: '32px',
    marginBottom: '24px',
  };

  const axisBadge = (color) => {
    const rgbVar = color.replace(/\)$/, '-rgb)');
    return ({
      display: 'inline-block',
      padding: '2px 12px',
      borderRadius: '9999px',
      fontSize: '13px',
      fontWeight: 600,
      background: `rgba(${rgbVar}, ${ALPHA_BG_LIGHT})`,
      color: color,
      marginRight: '8px',
    });
  };

  const exampleBox = {
    background: 'var(--bg-muted)',
    borderRadius: '12px',
    padding: '12px 16px',
    fontSize: '15px',
    lineHeight: '1.7',
    color: 'var(--text)',
    marginTop: '8px',
  };

  const cogItem = (severity, color) => {
    const rgbVar = color.replace(/\)$/, '-rgb)');
    return ({
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '16px',
      borderRadius: '16px',
      background: `rgba(${rgbVar}, ${ALPHA_BG_SUBTLE})`,
      border: `1px solid rgba(${rgbVar}, ${ALPHA_BORDER})`,
      marginBottom: '12px',
    });
  };

  const severityBadge = (level) => {
    // Index ordering MUST match: success/warning/accent/danger
    const colors    = ['var(--success)', 'var(--warning)', 'var(--accent)', 'var(--danger)'];
    const colorsRgb = ['var(--success-rgb)', 'var(--warning-rgb)', 'var(--accent-rgb)', 'var(--danger-rgb)'];
    const labels = [t('pace.scale0'), t('pace.scale1'), t('pace.scale2'), t('pace.scale3')];
    return (
      <span style={{
        display: 'inline-block', padding: '2px 10px', borderRadius: '9999px',
        fontSize: '12px', fontWeight: 600,
        background: `rgba(${colorsRgb[level]}, ${ALPHA_BG_MEDIUM})`, color: colors[level],
      }}>
        <FuriganaText text={labels[level]} />
      </span>
    );
  };

  return (
    <div className="min-h-screen py-12 px-4" style={{ background: 'var(--bg-base)', fontFamily: "'MFW-UDDigiKyokashoProN-Regular', 'UDデジタル教科書体', sans-serif", fontSize: '16px' }}>
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>
        {/* ヘッダー */}
        <div className="flex items-center justify-between" style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FuriganaText text={t('pace.guideTitle')} />
          </h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-secondary rounded-full"
          >
            ← <FuriganaText text={t('pace.backToDashboard')} />
          </button>
        </div>

        <p style={{ fontSize: '16px', lineHeight: 1.8, color: 'var(--text-muted)', marginBottom: '32px' }}>
          {renderRichFurigana(t('pace.intro'))}
        </p>

        {/* ====== 呈現モード偏好（観察値） ====== */}
        <div style={sectionCard}>
          <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text)', marginTop: 0, marginBottom: '12px' }}>
            <FuriganaText text={t('pace.ppSectionTitle')} />
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '20px', lineHeight: 1.7 }}>
            {renderRichFurigana(t('pace.ppSectionDesc'))}
          </p>

          {/* narrative */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '17px', fontWeight: 700, color: 'var(--primary)', marginBottom: '8px' }}>
              <span style={axisBadge('var(--primary)')}>N</span> <FuriganaText text={t('pace.pp.narrative.title')} />
            </h3>
            <div style={{ paddingLeft: '16px' }}>
              <p style={{ margin: '0 0 6px', lineHeight: 1.7, color: 'var(--text)' }}>
                <FuriganaText text={t('pace.pp.narrative.desc')} />
              </p>
              <div style={exampleBox}><FuriganaText text={t('pace.pp.narrative.example')} /></div>
            </div>
          </div>

          {/* step_by_step */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '17px', fontWeight: 700, color: 'var(--accent)', marginBottom: '8px' }}>
              <span style={axisBadge('var(--accent)')}>S</span> <FuriganaText text={t('pace.pp.step.title')} />
            </h3>
            <div style={{ paddingLeft: '16px' }}>
              <p style={{ margin: '0 0 6px', lineHeight: 1.7, color: 'var(--text)' }}>
                <FuriganaText text={t('pace.pp.step.desc')} />
              </p>
              <div style={exampleBox}><FuriganaText text={t('pace.pp.step.example')} /></div>
            </div>
          </div>

          {/* iconographic */}
          <div>
            <h3 style={{ fontSize: '17px', fontWeight: 700, color: 'var(--success)', marginBottom: '8px' }}>
              <span style={axisBadge('var(--success)')}>I</span> <FuriganaText text={t('pace.pp.icon.title')} />
            </h3>
            <div style={{ paddingLeft: '16px' }}>
              <p style={{ margin: '0 0 6px', lineHeight: 1.7, color: 'var(--text)' }}>
                <FuriganaText text={t('pace.pp.icon.desc')} />
              </p>
              <div style={exampleBox}><FuriganaText text={t('pace.pp.icon.example')} /></div>
            </div>
          </div>

          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '20px', lineHeight: 1.6, fontStyle: 'italic' }}>
            <FuriganaText text={t('pace.ppNote')} />
          </p>
        </div>

        {/* ====== 6+2 認知困難 ====== */}
        <div style={sectionCard}>
          <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text)', marginTop: 0, marginBottom: '8px' }}>
            <FuriganaText text={t('pace.cogSectionTitle')} />
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '20px' }}>
<FuriganaText text={t('pace.scaleLabel')} /> {severityBadge(0)} {severityBadge(1)} {severityBadge(2)} {severityBadge(3)}
          </p>

          <h3 style={{ fontSize: '17px', fontWeight: 700, color: 'var(--text)', marginBottom: '16px' }}><FuriganaText text={t('pace.coreTitle')} /></h3>

          {/* 1. working_memory */}
          <div style={cogItem(0, 'var(--primary)')}>
            <div style={{ flex: '0 0 28px', fontWeight: 700, color: 'var(--primary)', fontSize: '18px' }}>1</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--text)', marginBottom: '4px' }}><FuriganaText text={t('pace.cog.working_memory.name')} /></div>
              <div style={{ fontSize: '15px', color: 'var(--text)', lineHeight: 1.7 }}><FuriganaText text={t('pace.cog.working_memory.desc')} /></div>
              <div style={exampleBox}><FuriganaText text={t('pace.cog.working_memory.example')} /></div>
            </div>
          </div>

          {/* 2. visuospatial */}
          <div style={cogItem(0, 'var(--accent)')}>
            <div style={{ flex: '0 0 28px', fontWeight: 700, color: 'var(--accent)', fontSize: '18px' }}>2</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--text)', marginBottom: '4px' }}><FuriganaText text={t('pace.cog.visuospatial.name')} /></div>
              <div style={{ fontSize: '15px', color: 'var(--text)', lineHeight: 1.7 }}><FuriganaText text={t('pace.cog.visuospatial.desc')} /></div>
              <div style={exampleBox}><FuriganaText text={t('pace.cog.visuospatial.example')} /></div>
            </div>
          </div>

          {/* 3. crossmodal */}
          <div style={cogItem(0, 'var(--success)')}>
            <div style={{ flex: '0 0 28px', fontWeight: 700, color: 'var(--success)', fontSize: '18px' }}>3</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--text)', marginBottom: '4px' }}><FuriganaText text={t('pace.cog.crossmodal.name')} /></div>
              <div style={{ fontSize: '15px', color: 'var(--text)', lineHeight: 1.7 }}><FuriganaText text={t('pace.cog.crossmodal.desc')} /></div>
              <div style={exampleBox}><FuriganaText text={t('pace.cog.crossmodal.example')} /></div>
            </div>
          </div>

          {/* 4. load_vulnerability */}
          <div style={cogItem(0, 'var(--warning)')}>
            <div style={{ flex: '0 0 28px', fontWeight: 700, color: 'var(--warning)', fontSize: '18px' }}>4</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--text)', marginBottom: '4px' }}><FuriganaText text={t('pace.cog.load_vulnerability.name')} /></div>
              <div style={{ fontSize: '15px', color: 'var(--text)', lineHeight: 1.7 }}><FuriganaText text={t('pace.cog.load_vulnerability.desc')} /></div>
              <div style={exampleBox}><FuriganaText text={t('pace.cog.load_vulnerability.example')} /></div>
            </div>
          </div>

          {/* 5. executive_inhibition */}
          <div style={cogItem(0, 'var(--danger)')}>
            <div style={{ flex: '0 0 28px', fontWeight: 700, color: 'var(--danger)', fontSize: '18px' }}>5</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--text)', marginBottom: '4px' }}><FuriganaText text={t('pace.cog.executive_inhibition.name')} /></div>
              <div style={{ fontSize: '15px', color: 'var(--text)', lineHeight: 1.7 }}><FuriganaText text={t('pace.cog.executive_inhibition.desc')} /></div>
              <div style={exampleBox}><FuriganaText text={t('pace.cog.executive_inhibition.example')} /></div>
            </div>
          </div>

          {/* 6. language_semantic */}
          <div style={cogItem(0, 'var(--primary-strong)')}>
            <div style={{ flex: '0 0 28px', fontWeight: 700, color: 'var(--primary-strong)', fontSize: '18px' }}>6</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--text)', marginBottom: '4px' }}><FuriganaText text={t('pace.cog.language_semantic.name')} /></div>
              <div style={{ fontSize: '15px', color: 'var(--text)', lineHeight: 1.7 }}><FuriganaText text={t('pace.cog.language_semantic.desc')} /></div>
              <div style={exampleBox}><FuriganaText text={t('pace.cog.language_semantic.example')} /></div>
            </div>
          </div>

          {/* 修飾因子 */}
          <h3 style={{ fontSize: '17px', fontWeight: 700, color: 'var(--text)', margin: '28px 0 8px', paddingTop: '20px', borderTop: '1px solid var(--border)' }}>
            <FuriganaText text={t('pace.modifierTitle')} />
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '16px', lineHeight: 1.7 }}>
            <FuriganaText text={t('pace.modifierDesc')} />
          </p>

          {/* 7. math_anxiety */}
          <div style={cogItem(0, 'var(--danger)')}>
            <div style={{ flex: '0 0 28px', fontWeight: 700, color: 'var(--danger)', fontSize: '18px' }}>7</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--text)', marginBottom: '4px' }}><FuriganaText text={t('pace.modifier.math_anxiety.name')} /></div>
              <div style={{ fontSize: '15px', color: 'var(--text)', lineHeight: 1.7 }}><FuriganaText text={t('pace.modifier.math_anxiety.desc')} /></div>
              <div style={exampleBox}><FuriganaText text={t('pace.modifier.math_anxiety.example')} /></div>
            </div>
          </div>

          {/* 8. graph_literacy */}
          <div style={cogItem(0, 'var(--accent)')}>
            <div style={{ flex: '0 0 28px', fontWeight: 700, color: 'var(--accent)', fontSize: '18px' }}>8</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--text)', marginBottom: '4px' }}><FuriganaText text={t('pace.modifier.graph_literacy.name')} /></div>
              <div style={{ fontSize: '15px', color: 'var(--text)', lineHeight: 1.7 }}><FuriganaText text={t('pace.modifier.graph_literacy.desc')} /></div>
              <div style={exampleBox}><FuriganaText text={t('pace.modifier.graph_literacy.example')} /></div>
            </div>
          </div>
        </div>

        {/* ====== 学生情報の影響 ====== */}
        <div style={sectionCard}>
          <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text)', marginTop: 0, marginBottom: '20px' }}>
            <FuriganaText text={t('pace.influenceTitle')} />
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            <div style={{ padding: '20px', borderRadius: '16px', background: 'var(--primary-bg)', border: '1px solid var(--border)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--primary)', marginTop: 0, marginBottom: '8px' }}><FuriganaText text={t('pace.influence.gender.title')} /></h3>
              <p style={{ fontSize: '15px', lineHeight: 1.7, color: 'var(--text)', margin: 0 }}>
                <FuriganaText text={t('pace.influence.gender.desc')} />
              </p>
            </div>

            <div style={{ padding: '20px', borderRadius: '16px', background: 'rgba(132,63,31,0.06)', border: '1px solid var(--border)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--accent)', marginTop: 0, marginBottom: '8px' }}><FuriganaText text={t('pace.influence.experience.title')} /></h3>
              <p style={{ fontSize: '15px', lineHeight: 1.7, color: 'var(--text)', margin: 0 }}>
                <FuriganaText text={t('pace.influence.experience.desc')} />
              </p>
            </div>

            <div style={{ padding: '20px', borderRadius: '16px', background: 'rgba(47,107,62,0.06)', border: '1px solid var(--border)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--success)', marginTop: 0, marginBottom: '8px' }}><FuriganaText text={t('pace.influence.interest.title')} /></h3>
              <p style={{ fontSize: '15px', lineHeight: 1.7, color: 'var(--text)', margin: 0 }}>
                <FuriganaText text={t('pace.influence.interest.desc')} />
              </p>
            </div>

            <div style={{ padding: '20px', borderRadius: '16px', background: 'var(--warm-bg)', border: '1px solid var(--border)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--warning)', marginTop: 0, marginBottom: '8px' }}><FuriganaText text={t('pace.influence.personality.title')} /></h3>
              <p style={{ fontSize: '15px', lineHeight: 1.7, color: 'var(--text)', margin: 0 }}>
                <FuriganaText text={t('pace.influence.personality.desc')} />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Profile() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fa = useFuriganaAria();
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // 完整的プロフィール数据
  const [profile, setProfile] = useState(null);

  // 编辑模式
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    gender: '',
    interests: [],
    personalityTraits: [],
    background: ''
  });

  // 新增项临时输入
  const [newInterest, setNewInterest] = useState('');
  const [newTrait, setNewTrait] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    authAPI.getCurrentUser().then(res => {
      setUserRole(res.data?.role || 'student');
    }).catch(() => setUserRole('student'));
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError('');  // 门下省 C4: clear stale error so success render doesn't show old banner
      const response = await api.get('/api/profile/complete');
      setProfile(response.data);

      // 初始化编辑数据
      setEditData({
        gender: response.data.persona?.gender || '',
        interests: response.data.persona?.interests || [],
        personalityTraits: response.data.persona?.personality_traits || [],
        background: response.data.persona?.background || ''
      });
    } catch (error) {
      logClientError('Failed to load profile:', error);
      setError('profile.loadFailed');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setMessage('');

      await api.put('/api/profile/persona', {
        gender: editData.gender,
        interests: editData.interests,
        personality_traits: editData.personalityTraits,
        background: editData.background
      });

      setMessage('profile.saved');
      setIsEditing(false);
      await loadProfile();
    } catch (error) {
      logClientError('Failed to save profile:', error);
      setError('profile.saveFailed');
    } finally {
      setSaving(false);
    }
  };

  const handleAnalyze = async () => {
    try {
      setAnalyzing(true);
      setError('');
      setMessage('');

      // ICCHP §6.5: API は呈現モード偏好（softmax 確率）を返すようになりました
      const response = await api.post('/api/profile/analyze-learning-style');

      setMessage(t('profile.ppObserved', { count: response.data.conversation_count }));
      await loadProfile();
    } catch (error) {
      logClientError('Failed to analyze presentation preference:', error);
      setError('profile.analyzeFailed');
    } finally {
      setAnalyzing(false);
    }
  };

  const addInterest = () => {
    if (newInterest.trim() && !editData.interests.includes(newInterest.trim())) {
      setEditData({
        ...editData,
        interests: [...editData.interests, newInterest.trim()]
      });
      setNewInterest('');
    }
  };

  const removeInterest = (interest) => {
    setEditData({
      ...editData,
      interests: editData.interests.filter(i => i !== interest)
    });
  };

  const addTrait = () => {
    if (newTrait.trim() && !editData.personalityTraits.includes(newTrait.trim())) {
      setEditData({
        ...editData,
        personalityTraits: [...editData.personalityTraits, newTrait.trim()]
      });
      setNewTrait('');
    }
  };

  const removeTrait = (trait) => {
    setEditData({
      ...editData,
      personalityTraits: editData.personalityTraits.filter(t => t !== trait)
    });
  };

  if (loading || userRole === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-base)' }}>
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('common.loading')} /></p>
        </div>
      </div>
    );
  }

  if (userRole === 'teacher') {
    return <TeacherPACEGuide />;
  }

  // HEADER: keep in sync with main return L452-462 (per 门下省 C5)
  // Outer guard against null profile (loadProfile catch path) — prevents student
  // render reaching L549+ unguarded `profile.persona?.x` and crashing the subtree
  // before the existing L473-480 error banner can paint. role="alert" + a11y
  // pattern matches v2.14.4 P1-2 P3 hotfix (TeacherDashboard.js:851).
  if (!profile) {
    return (
      <div className="min-h-screen py-12 px-4" style={{ background: 'var(--bg-base)' }}>
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold flex items-center gap-2" style={{ color: 'var(--primary)' }}>
              <span>👤</span> <FuriganaText text={t('profile.title')} />
            </h1>
            <button onClick={() => navigate('/dashboard')} className="btn-secondary rounded-full">
              ← <FuriganaText text={t('pace.backToDashboard')} />
            </button>
          </div>
          <div
            role="alert"
            data-testid="profile-load-error"
            className="mb-6 p-4 rounded-2xl text-sm"
            style={{ border: '2px solid var(--danger)', background: 'var(--danger-bg)', color: 'var(--danger)' }}
          >
            <FuriganaText text={t(error || 'profile.loadFailed')} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" style={{ background: 'var(--bg-base)' }}>
      <div className="max-w-screen-2xl mx-auto">
        {/* ヘッダー */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-2" style={{ color: 'var(--primary)' }}>
            <span>👤</span> <FuriganaText text={t('profile.title')} />
          </h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-secondary rounded-full"
          >
            ← <FuriganaText text={t('pace.backToDashboard')} />
          </button>
        </div>

        {/* メッセージ表示 */}
        {message && (
          <div
            role="status"
            className="mb-6 p-4 rounded-2xl text-sm"
            style={{ border: '2px solid var(--success)', background: 'rgba(47,107,62,0.08)', color: 'var(--success)' }}
          >
            <FuriganaText text={t(message)} />
          </div>
        )}
        {error && (
          <div
            role="alert"
            className="mb-6 p-4 rounded-2xl text-sm"
            style={{ border: '2px solid var(--danger)', background: 'var(--danger-bg)', color: 'var(--danger)' }}
          >
            <FuriganaText text={t(error)} />
          </div>
        )}

        {/* 3カラムレイアウト: ペルソナ+基本情報+PACE | 学習スタイル | 認知特性 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* 列1: ペルソナ情報 + 基本情報 + PACE説明 */}
          <div className="space-y-4">
            {/* 基本情報 */}
            <div className="rounded-[24px] p-6 overflow-hidden" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)' }}>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text)' }}>
                <span>📋</span> <FuriganaText text={t('profile.basicInfo')} />
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('register.summaryParticipantCode')} /></span>
                  {/* unverified proper name → bare (never-guess), Codex R19 #2 */}
                  <span className="font-medium">{profile?.basic_info?.username}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('register.summaryGrade')} /></span>
                  <span className="font-medium">{
                    GRADE_VALUE_TO_LABEL_KEY[profile?.basic_info?.grade_level]
                      ? <FuriganaText text={t(GRADE_VALUE_TO_LABEL_KEY[profile.basic_info.grade_level])} />
                      : (profile?.basic_info?.grade_level || '')
                  }</span>
                </div>
              </div>
            </div>

            {/* ペルソナ情報（手動入力） */}
            <div className="rounded-[24px] p-6 overflow-hidden" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)' }}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: 'var(--text)' }}>
                  <span>✏️</span> <FuriganaText text={t('profile.personaInfo')} />
                </h2>
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn-primary rounded-full text-sm"
                >
                  <FuriganaText text={t('preset.edit')} />
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="rounded-full px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50"
                    style={{ background: 'var(--success)', color: 'var(--text-on-primary)' }}
                  >
                    <FuriganaText text={saving ? t('profile.saving') : t('common.save')} />
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditData({
                        gender: profile.persona?.gender || '',
                        interests: profile.persona?.interests || [],
                        personalityTraits: profile.persona?.personality_traits || [],
                        background: profile.persona?.background || ''
                      });
                    }}
                    className="btn-secondary rounded-full text-sm"
                  >
                    <FuriganaText text={t('common.cancel')} />
                  </button>
                </div>
              )}
            </div>

            {/* 性別 */}
            <div className="mb-4">
              <label htmlFor="profile-gender-select" className="block text-sm font-medium mb-2" style={{ color: 'var(--text)' }}><FuriganaText text={t('profile.genderLabel')} /></label>
              {!isEditing ? (
                <div className="px-4 py-2 rounded-xl" style={{ background: 'var(--bg-muted)' }}>
                  <FuriganaText text={profile.persona?.gender === 'male' ? t('profile.gender.male') :
                   profile.persona?.gender === 'female' ? t('profile.gender.female') :
                   profile.persona?.gender === 'other' ? t('profile.gender.other') : t('profile.unset')} />
                </div>
              ) : (
                <select
                  id="profile-gender-select"
                  value={editData.gender}
                  onChange={(e) => setEditData({...editData, gender: e.target.value})}
                  className="input-field rounded-xl"
                >
                  <option value="">{t('profile.selectPlease')}</option>
                  <option value="male">{t('profile.gender.male')}</option>
                  <option value="female">{t('profile.gender.female')}</option>
                  <option value="other">{t('profile.gender.other')}</option>
                </select>
              )}
            </div>

            {/* 興味 */}
            <div className="mb-4">
              <label htmlFor="profile-interest-input" className="block text-sm font-medium mb-2" style={{ color: 'var(--text)' }}><FuriganaText text={t('profile.interestsLabel')} /></label>
              <div className="flex flex-wrap gap-2 mb-2">
                {(isEditing ? editData.interests : profile.persona?.interests || []).map((interest, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 rounded-full text-sm flex items-center gap-1"
                    style={{ background: 'var(--primary-bg)', color: 'var(--primary)', border: '1px solid var(--primary-light)' }}
                  >
                    <FuriganaText text={interest} />
                    {isEditing && (
                      <button
                        onClick={() => removeInterest(interest)}
                        className="ml-1 font-bold"
                        style={{ color: 'var(--danger)' }}
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {isEditing && (
                <div className="flex gap-2">
                  <input
                    id="profile-interest-input"
                    type="text"
                    value={newInterest}
                    onChange={(e) => setNewInterest(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addInterest()}
                    placeholder={fa(t('profile.interestsPlaceholder'))}
                    className="input-field flex-1 rounded-xl text-sm"
                  />
                  <button
                    onClick={addInterest}
                    className="btn-primary rounded-full text-sm"
                  >
                    <FuriganaText text={t('profile.add')} />
                  </button>
                </div>
              )}
            </div>

            {/* 性格特性 */}
            <div className="mb-4">
              <label htmlFor="profile-trait-input" className="block text-sm font-medium mb-2" style={{ color: 'var(--text)' }}><FuriganaText text={t('profile.traitsLabel')} /></label>
              <div className="flex flex-wrap gap-2 mb-2">
                {(isEditing ? editData.personalityTraits : profile.persona?.personality_traits || []).map((trait, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 rounded-full text-sm flex items-center gap-1"
                    style={{ background: 'rgba(47,107,62,0.08)', color: 'var(--success)', border: '1px solid var(--success)' }}
                  >
                    <FuriganaText text={trait} />
                    {isEditing && (
                      <button
                        onClick={() => removeTrait(trait)}
                        className="ml-1 font-bold"
                        style={{ color: 'var(--danger)' }}
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {isEditing && (
                <div className="flex gap-2">
                  <input
                    id="profile-trait-input"
                    type="text"
                    value={newTrait}
                    onChange={(e) => setNewTrait(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTrait()}
                    placeholder={fa(t('profile.traitsPlaceholder'))}
                    className="input-field flex-1 rounded-xl text-sm"
                  />
                  <button
                    onClick={addTrait}
                    className="rounded-full px-4 py-2 text-sm font-semibold transition-colors"
                    style={{ background: 'var(--success)', color: 'var(--text-on-primary)' }}
                  >
                    <FuriganaText text={t('profile.add')} />
                  </button>
                </div>
              )}
            </div>

            {/* 背景・経験 */}
            <div>
              <label htmlFor="profile-background-textarea" className="block text-sm font-medium mb-2" style={{ color: 'var(--text)' }}><FuriganaText text={t('profile.backgroundLabel')} /></label>
              {!isEditing ? (
                <div className="px-4 py-2 rounded-xl min-h-[80px]" style={{ background: 'var(--bg-muted)' }}>
                  {profile.persona?.background ? <FuriganaText text={profile.persona.background} /> : <FuriganaText text={t('profile.unset')} />}
                </div>
              ) : (
                <textarea
                  id="profile-background-textarea"
                  value={editData.background}
                  onChange={(e) => setEditData({...editData, background: e.target.value})}
                  placeholder={fa(t('profile.backgroundPlaceholder'))}
                  rows="3"
                  className="input-field rounded-xl"
                />
              )}
              </div>
            </div>

            {/* PACE説明カード */}
            <div className="rounded-2xl p-6 overflow-hidden" style={{ background: 'var(--primary-bg)', border: '2px solid var(--border)' }}>
              <h3 className="font-bold mb-2 flex items-center gap-2" style={{ color: 'var(--primary-strong)' }}>
                <span>💡</span> <FuriganaText text={t('profile.paceCardTitle')} />
              </h3>
              <p className="text-sm" style={{ color: 'var(--text)' }}>
                {renderRichFurigana(t('profile.paceCardBody'))}
              </p>
            </div>
          </div>

          {/* 列2: 学習スタイル（自動分析） */}
          <div className="rounded-[24px] p-6 overflow-hidden" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: 'var(--text)' }}>
                <span>🧠</span> <FuriganaText text={t('profile.learningStyleTitle')} />
              </h2>
              <button
                onClick={handleAnalyze}
                disabled={analyzing}
                className="rounded-full px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
                style={{ background: 'var(--accent)', color: 'var(--text-on-primary)' }}
              >
                {analyzing ? (
                  <>
                    <span className="animate-spin">⏳</span> <FuriganaText text={t('profile.analyzing')} />
                  </>
                ) : (
                  <>
                    <span>🔍</span> <FuriganaText text={t('profile.autoAnalyze')} />
                  </>
                )}
              </button>
            </div>

            {/*
              ICCHP 2026 §6.5: 固定タイプ分類は廃止されました。
              代わりに観察された呈現モード偏好（softmax 確率）を表示します。
              これは固定タイプ分類ではなく、観察された一時的傾向です。
            */}
            {profile?.presentation_preference ? (
              <>
                {/* narrative */}
                <div className="mb-3 p-4 rounded-xl" style={{ background: 'var(--primary-bg)', border: '1px solid var(--border)' }}>
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--text)' }}><FuriganaText text={t('pace.pp.narrative.title')} /></div>
                  <div className="flex items-center justify-between">
                    <div className="text-base font-bold" style={{ color: 'var(--primary)' }}>
                      📝 {Math.round((profile.presentation_preference.narrative || 0) * 100)}%
                    </div>
                  </div>
                  <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    <FuriganaText text={t('profile.ppShort.narrative')} />
                  </div>
                </div>

                {/* step_by_step */}
                <div className="mb-3 p-4 rounded-xl" style={{ background: 'rgba(132,63,31,0.06)', border: '1px solid var(--border)' }}>
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--text)' }}><FuriganaText text={t('pace.pp.step.title')} /></div>
                  <div className="flex items-center justify-between">
                    <div className="text-base font-bold" style={{ color: 'var(--accent)' }}>
                      🪜 {Math.round((profile.presentation_preference.step_by_step || 0) * 100)}%
                    </div>
                  </div>
                  <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    <FuriganaText text={t('profile.ppShort.step')} />
                  </div>
                </div>

                {/* iconographic */}
                <div className="mb-3 p-4 rounded-xl" style={{ background: 'rgba(47,107,62,0.06)', border: '1px solid var(--border)' }}>
                  <div className="text-sm font-medium mb-1" style={{ color: 'var(--text)' }}><FuriganaText text={t('pace.pp.icon.title')} /></div>
                  <div className="flex items-center justify-between">
                    <div className="text-base font-bold" style={{ color: 'var(--success)' }}>
                      🖼️ {Math.round((profile.presentation_preference.iconographic || 0) * 100)}%
                    </div>
                  </div>
                  <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    <FuriganaText text={t('profile.ppShort.icon')} />
                  </div>
                </div>

                {/* 注記 */}
                <div className="mb-3 p-3 rounded-xl" style={{ background: 'var(--warm-bg)', border: '1px dashed var(--border)' }}>
                  <div className="text-xs" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    <FuriganaText text={t('profile.ppNote2')} />
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
                <div className="text-4xl mb-2">🔍</div>
                <p><FuriganaText text={t('profile.ppNotObserved')} /></p>
                <p className="text-sm mt-2">
                  <FuriganaText text={t('profile.ppHint')} />
                </p>
                <p className="text-xs mt-2" style={{ fontStyle: 'italic' }}>
                  <FuriganaText text={t('profile.ppIcchpNote')} />
                </p>
              </div>
            )}
          </div>

          {/* 列3: 認知特性 */}
          {profile?.cognitive_difficulty ? (
            <div className="rounded-[24px] p-6 overflow-hidden" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)' }}>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text)' }}>
                <span>🧠</span> <FuriganaText text={t('profile.cogTitle')} />
              </h2>
              {(() => {
                const cd = profile.cognitive_difficulty;
                const items = [
                  { label: t('profile.cog.working_memory'), score: cd.working_memory },
                  { label: t('profile.cog.visuospatial'), score: cd.visuospatial },
                  { label: t('profile.cog.crossmodal'), score: cd.crossmodal },
                  { label: t('profile.cog.load_vulnerability'), score: cd.load_vulnerability },
                  { label: t('profile.cog.executive_inhibition'), score: cd.executive_inhibition || 0 },
                  { label: t('profile.cog.language_semantic'), score: cd.language_semantic || 0 },
                ];
                const modifiers = [
                  { label: t('profile.cog.math_anxiety'), score: cd.math_anxiety || 0 },
                  { label: t('profile.cog.graph_literacy'), score: cd.graph_literacy || 0 },
                ];
                const severityLabels = [t('register.severity.none'), t('register.severity.mild'), t('register.severity.moderate'), t('register.severity.severe')];
                // Index ordering MUST match: success/warning/accent/danger
                const severityColors    = ['var(--success)', 'var(--warning)', 'var(--accent)', 'var(--danger)'];
                const severityColorsRgb = ['var(--success-rgb)', 'var(--warning-rgb)', 'var(--accent-rgb)', 'var(--danger-rgb)'];
                return (
                  <div className="space-y-3">
                    {items.map(item => (
                      <div key={item.label}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span style={{ color: 'var(--text)' }}><FuriganaText text={item.label} /></span>
                          <span
                            className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                            style={{
                              background: `rgba(${severityColorsRgb[item.score]}, ${ALPHA_BG_MEDIUM})`,
                              color: severityColors[item.score],
                            }}
                          >
                            <FuriganaText text={severityLabels[item.score]} />
                          </span>
                        </div>
                        <div className="h-2 rounded-full" style={{ background: 'var(--bg-muted)' }}>
                          <div
                            className="h-2 rounded-full transition-all"
                            style={{
                              width: `${(item.score / 3) * 100}%`,
                              background: severityColors[item.score],
                              minWidth: item.score > 0 ? '8%' : '0%',
                            }}
                          />
                        </div>
                      </div>
                    ))}

                    {/* 調整因子 */}
                    <div style={{ borderTop: '1px solid var(--border)', marginTop: '8px', paddingTop: '12px' }}>
                      <p className="text-xs font-bold mb-3" style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('register.modifiers')} /></p>
                      {modifiers.map(item => (
                        <div key={item.label} className="mb-3">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span style={{ color: 'var(--text)' }}><FuriganaText text={item.label} /></span>
                            <span
                              className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                              style={{
                                background: `rgba(${severityColorsRgb[item.score]}, ${ALPHA_BG_MEDIUM})`,
                                color: severityColors[item.score],
                              }}
                            >
                              <FuriganaText text={severityLabels[item.score]} />
                            </span>
                          </div>
                          <div className="h-2 rounded-full" style={{ background: 'var(--bg-muted)' }}>
                            <div
                              className="h-2 rounded-full transition-all"
                              style={{
                                width: `${(item.score / 3) * 100}%`,
                                background: severityColors[item.score],
                                minWidth: item.score > 0 ? '8%' : '0%',
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="rounded-[24px] p-6 overflow-hidden flex items-center justify-center" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)' }}>
              <div className="text-center" style={{ color: 'var(--text-muted)' }}>
                <div className="text-4xl mb-2">🧠</div>
                <p><FuriganaText text={t('profile.noCogData')} /></p>
              </div>
            </div>
          )}

        </div>

        {/* ICCHP 2026 §6.4 (3): より詳しいプロファイル設定 (任意, Session>=3 のみ) */}
        <ILSOptInSection profile={profile} reload={loadProfile} setMessage={setMessage} setError={setError} />
      </div>
    </div>
  );
}


// ==================== ILS Opt-In Section (任意, Session>=3 のみ) ====================
//
// ICCHP 2026 §6.4 (3) 準拠: Session>=3 で ILS 44 問の任意回答リンクを表示。
// 教師端 (TeacherPACEGuide) では表示しない。
function ILSOptInSection({ profile, reload, setMessage, setError }) {
  const { t, i18n } = useTranslation();
  const [showQuestions, setShowQuestions] = useState(false);
  const [ilsQuestions, setIlsQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!profile?.basic_info) return null;
  // 教師は表示しない
  if ((profile.basic_info.role || 'student') === 'teacher') return null;

  const sessionCount = profile.basic_info.session_count || 0;
  const completedAt = profile.basic_info.ils_opt_in_completed_at || null;

  // Session<3 はリンク非表示 (まだ早期段階)
  if (sessionCount < 3) {
    return null;
  }

  const handleStart = async () => {
    setLoading(true);
    try {
      const resp = await api.get('/api/questionnaire?mode=ils_opt_in');
      setIlsQuestions(resp.data.ils_questions || []);
      setShowQuestions(true);
    } catch (e) {
      logClientError('Failed to load ILS questions:', e);
      setError('profile.ils.loadFailed');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (qid, val) => {
    setAnswers(prev => ({ ...prev, [qid]: val }));
  };

  const allAnswered = ilsQuestions.length > 0 && ilsQuestions.every(q => answers[q.id] !== undefined);

  const handleSubmit = async () => {
    if (!allAnswered) {
      setError('register.error.answerAll');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/api/profile/ils-opt-in', { ils_answers: answers });
      setMessage('profile.ils.saved');
      setShowQuestions(false);
      setAnswers({});
      await reload();
    } catch (e) {
      logClientError('ILS opt-in submission failed:', e);
      setError('profile.saveFailed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="mt-8 rounded-[24px] p-6"
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-md)',
      }}
    >
      <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--text)' }}>
        <FuriganaText text={t('profile.ils.title')} />
      </h2>
      <p className="text-sm mb-4" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
        <FuriganaText text={t('profile.ils.desc', { count: sessionCount })} />
      </p>

      {completedAt && (
        <div
          className="mb-4 rounded-2xl p-3 text-sm"
          style={{
            border: '1px solid var(--success)',
            background: 'rgba(47,107,62,0.08)',
            color: 'var(--success)',
          }}
        >
          ✓ <FuriganaText text={t('profile.ils.alreadyDone')} />: {new Date(completedAt).toLocaleString(i18n.language === 'ja' ? 'ja-JP' : 'en-US')}
        </div>
      )}

      {!showQuestions ? (
        <button
          type="button"
          onClick={handleStart}
          disabled={loading}
          className="btn-primary rounded-full px-6 py-2 text-sm transition-transform hover:scale-[1.02] disabled:opacity-60"
        >
          <FuriganaText text={loading ? t('common.loading') : (completedAt ? t('profile.ils.retake') : t('profile.ils.start'))} />
        </button>
      ) : (
        <div>
          <div
            className="mb-4 rounded-2xl p-3 text-xs"
            style={{
              border: '1px solid var(--accent)',
              background: 'rgba(132,63,31,0.06)',
              color: 'var(--text)',
            }}
          >
            <strong style={{ color: 'var(--accent)' }}><FuriganaText text={t('profile.ils.count', { count: ilsQuestions.length })} /></strong>{' '}
            <FuriganaText text={t('profile.ils.instruction')} />
          </div>

          <div className="space-y-3 mb-4">
            {ilsQuestions.map((q, idx) => (
              <ILSCard
                key={q.id}
                question={q}
                index={idx}
                selected={answers[q.id]}
                onSelect={(val) => handleAnswer(q.id, val)}
              />
            ))}
          </div>

          <div className="flex gap-3 justify-center">
            <button
              type="button"
              onClick={() => { setShowQuestions(false); setAnswers({}); }}
              className="btn-secondary rounded-full px-6 py-2 text-sm"
            >
              <FuriganaText text={t('common.cancel')} />
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              className="btn-primary rounded-full px-6 py-2 text-sm transition-transform hover:scale-[1.02] disabled:opacity-60"
            >
              <FuriganaText text={submitting ? t('profile.saving') : t('profile.ils.submit')} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function ILSCard({ question, index, selected, onSelect }) {
  return (
    <div
      className="rounded-2xl p-4"
      style={{
        border: `1px solid ${selected ? 'var(--primary)' : 'var(--border)'}`,
        background: selected ? 'var(--primary-bg)' : 'var(--bg-muted)',
      }}
    >
      <p className="mb-3 text-sm font-medium" style={{ color: 'var(--text)' }}>
        <span className="mr-2" style={{ color: 'var(--text-muted)' }}>Q{index + 1}.</span>
        {question.question}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onSelect('a')}
          className="flex-1 rounded-xl px-3 py-2.5 text-sm font-medium text-left transition-all"
          style={{
            border: `2px solid ${selected === 'a' ? 'var(--primary)' : 'var(--border)'}`,
            background: selected === 'a' ? 'var(--primary)' : 'transparent',
            color: selected === 'a' ? 'var(--text-on-primary)' : 'var(--text)',
          }}
        >
          <span className="mr-1.5 font-bold">A.</span>{question.option_a}
        </button>
        <button
          type="button"
          onClick={() => onSelect('b')}
          className="flex-1 rounded-xl px-3 py-2.5 text-sm font-medium text-left transition-all"
          style={{
            border: `2px solid ${selected === 'b' ? 'var(--primary)' : 'var(--border)'}`,
            background: selected === 'b' ? 'var(--primary)' : 'transparent',
            color: selected === 'b' ? 'var(--text-on-primary)' : 'var(--text)',
          }}
        >
          <span className="mr-1.5 font-bold">B.</span>{question.option_b}
        </button>
      </div>
    </div>
  );
}


export default Profile;
